import { NULL_AS_STRING } from '@repo/shared/dtos/beneficiary'
import {
  BooleanSchemaItem,
  DateSchemaItem,
  EnumSchemaItem,
  MultiselectSchemaItem,
  NumberSchemaItem,
  StringSchemaItem,
} from '@repo/shared/dtos/structured-data'
import { isEmptyString } from '@repo/shared/utils'
import { getLocale } from '@repo/ui/hooks'
import { z } from '@repo/ui/lang'
import { format, isValid, parseISO } from 'date-fns'

import { resetDateToUTCStartOfDayEpoch } from '@/components/beneficiary/list/filters/helpers'

const baseSchema = z.object({
  label: z.string().trim().nonempty('Label required'),
  instructionsToFillValue: z.string().optional(),
})

//
// Boolean Schema and Types
//
export type BooleanString = 'true' | 'false' | typeof NULL_AS_STRING

export const booleanValidationSchema = baseSchema.extend({
  type: z.literal('boolean'),
  defaultValue: z.literal('true').or(z.literal('false')).or(z.literal(NULL_AS_STRING)),
})

export type BooleanFormValues = z.infer<typeof booleanValidationSchema>

export function booleanNullToString(value: BooleanSchemaItem['defaultValue']): BooleanString {
  if (value === true) return 'true'
  if (value === false) return 'false'
  return NULL_AS_STRING
}

export function stringToBooleanNull(value: BooleanString): BooleanSchemaItem['defaultValue'] {
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

//
// String Schema and Types
//
export const stringValidationSchema = baseSchema.extend({
  type: z.literal('string'),
  defaultValue: z.string(),
})

export type StringFormValues = z.infer<typeof stringValidationSchema>

//
// Date Schema and Types
//
export const dateValidationSchema = baseSchema.extend({
  type: z.literal('date'),
  defaultValue: z.string(),
})

export type DateFormValues = z.infer<typeof dateValidationSchema>

export function dateNullToString(value: DateSchemaItem['defaultValue']): string {
  return value ? format(value, 'yyyy-MM-dd', { locale: getLocale() }) : NULL_AS_STRING
}

export function stringToDateNull(value: string): DateSchemaItem['defaultValue'] {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? resetDateToUTCStartOfDayEpoch(parsed) : null
}

//
// Number Schema and Types
//
export const numberValidationSchema = baseSchema.extend({
  type: z.literal('number'),
  defaultValue: z.string(),
})

export type NumberFormValues = z.infer<typeof numberValidationSchema>

export function numberNullToString(value: NumberSchemaItem['defaultValue']): string {
  return !value || isNaN(Number(value)) ? NULL_AS_STRING : String(value)
}

export function stringToNumberNull(value: string): NumberSchemaItem['defaultValue'] {
  if (!value) return null
  const parsed = Number(value)
  return isNaN(parsed) ? null : parsed
}

//
// Enum Schema and Types
//
export const enumValidationSchema = baseSchema
  .extend({
    type: z.literal('enum'),
    enumValues: z
      .array(z.string().trim())
      .min(2, 'At least 2 enum values are required')
      .refine(
        (arr) => {
          return arr.every((v) => !isEmptyString(v))
        },
        { message: 'Enum value cannot be empty' },
      )
      .refine(
        (arr) => {
          const lower = arr.map((v) => v.toLowerCase())
          return lower.length === new Set(lower).size
        },
        { message: 'Enum values must be unique' },
      ),
    defaultValue: z.string(),
  })
  .refine((props) => {
    const { enumValues, defaultValue } = props
    return [NULL_AS_STRING, ...enumValues].includes(defaultValue)
  })

export type EnumFormValues = z.infer<typeof enumValidationSchema>

export function stringNullToString(
  value: EnumSchemaItem['defaultValue'] | StringSchemaItem['defaultValue'],
): string {
  return !value ? NULL_AS_STRING : String(value)
}

export function stringToStringNull(
  value: string,
): EnumSchemaItem['defaultValue'] | StringSchemaItem['defaultValue'] {
  return value !== NULL_AS_STRING ? String(value) : null
}

//
// Multiselect Schema and Types
//
export const multiselectValidationSchema = baseSchema
  .extend({
    type: z.literal('multiselect'),
    enumValues: z
      .array(z.string().trim())
      .min(2, 'At least 2 enum values are required')
      .refine(
        (arr) => {
          return arr.every((v) => !isEmptyString(v))
        },
        { message: 'Enum value cannot be empty' },
      )
      .refine(
        (arr) => {
          const lower = arr.map((v) => v.toLowerCase())
          return lower.length === new Set(lower).size
        },
        { message: 'Enum values must be unique' },
      ),
    defaultValue: z.string(),
  })
  .refine((props) => {
    const { enumValues, defaultValue } = props
    const defaultValues = defaultValue.split(',').map((v) => v.trim())
    return [NULL_AS_STRING, ...enumValues].some((i) => defaultValues.includes(i))
  })

export type MultiselectFormValues = z.infer<typeof multiselectValidationSchema>

export function stringArrayNullToString(value: MultiselectSchemaItem['defaultValue']): string {
  return !value ? NULL_AS_STRING : value.map((v) => String(v)).join(', ')
}

export function stringToStringArrayNull(value: string): MultiselectSchemaItem['defaultValue'] {
  return value.includes(NULL_AS_STRING) ? null : value.split(',').map((v) => v.trim())
}
